import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Carousel as CarouselANTD } from 'antd';

import { AdvancedSearch } from '../AdvancedSearch';
import { useAuth } from '../../context/auth';
import { useSearch } from '../../context/search';
import { filtersToSearchParams } from '../../utils/searchUrlParams';
import { DEFAULT_SEARCH_FILTERS } from '../../types/searchFilters';
import { maskBRL } from '../../utils/mask';
import { IPropsBanners } from '../../types';
import {
  BannerMedia,
  BannerRoot,
  ClearFiltersButton,
  ExpandedFilters,
  ExpandedFiltersBody,
  ExpandedFiltersFooter,
  FieldLabel,
  FieldsRow,
  HeroCard,
  HeroColumn,
  HeroSubtitle,
  HeroTitle,
  MoreFiltersToggle,
  SearchButton,
} from './styles';

const PLACEHOLDER_IMG = '/images/placeholder-image-nogueira.png';

interface HomeHeroSearchProps {
  banners?: IPropsBanners[];
  totalEmpreendimentos?: number;
}

export function HomeHeroSearch({
  banners = [],
  totalEmpreendimentos,
}: HomeHeroSearchProps) {
  const navigate = useNavigate();
  const { api } = useAuth();
  const {
    filters,
    querySearch,
    setQuerySearch,
    updateFilter,
    setFilters,
    resetFilters,
  } = useSearch();

  const [moreFilters, setMoreFilters] = useState(false);
  const [construtoras, setConstrutoras] = useState<
    { label: string; value: string }[]
  >([]);
  const [localSearch, setLocalSearch] = useState(querySearch || '');
  const [maxPrice, setMaxPrice] = useState(() => {
    if (
      filters.max_sale_price &&
      filters.max_sale_price !== DEFAULT_SEARCH_FILTERS.max_sale_price
    ) {
      return filters.max_sale_price;
    }
    return 0;
  });
  const [rooms, setRooms] = useState(() => {
    const first = filters.qtd_rooms?.[0];
    if (!first) return '';
    if (first === '6+') return '6';
    return first;
  });

  useEffect(() => {
    api
      .get('/portal/constructors', { params: { portal: 'hub' } })
      .then((res) => {
        const list = (res.data ?? []).map((item: any) => ({
          label: item.nome || item.descricao || String(item.id),
          value: String(item.id),
        }));
        setConstrutoras(list);
      })
      .catch(() => undefined);
  }, [api]);

  const roundCount = (n?: number) => {
    if (!n || n <= 0) return null;
    if (n >= 1000) return `${Math.floor(n / 100) * 100}`;
    if (n >= 100) return `${Math.floor(n / 10) * 10}`;
    return String(n);
  };

  const rounded = roundCount(totalEmpreendimentos);
  const construtoraEmpty = !filters.id_constructor;

  const roomsToFilter = (value: string): string[] => {
    if (!value) return [];
    const n = Number(value);
    if (!Number.isFinite(n) || n < 1) return [];
    if (n >= 6) return ['6+'];
    return [String(Math.floor(n))];
  };

  const handleSearch = useCallback(() => {
    const nextRooms = roomsToFilter(rooms);
    const hasHeroPrice = maxPrice > 0;

    setQuerySearch(localSearch);
    setFilters((prev) => ({
      ...prev,
      query_search: localSearch,
      max_sale_price: hasHeroPrice ? maxPrice : prev.max_sale_price,
      min_sale_price: hasHeroPrice ? 0 : prev.min_sale_price,
      qtd_rooms: rooms === '' ? prev.qtd_rooms : nextRooms,
      is_sale: '1',
    }));

    const merged = {
      ...filters,
      query_search: localSearch,
      max_sale_price: hasHeroPrice ? maxPrice : filters.max_sale_price,
      min_sale_price: hasHeroPrice ? 0 : filters.min_sale_price,
      qtd_rooms: rooms === '' ? filters.qtd_rooms : nextRooms,
      is_sale: '1' as const,
    };

    const params = filtersToSearchParams(merged, localSearch);
    const qs = params.toString();
    navigate(`/resultado-pesquisa${qs ? `?${qs}` : ''}`);
  }, [
    rooms,
    maxPrice,
    localSearch,
    setQuerySearch,
    setFilters,
    filters,
    navigate,
  ]);

  const handleClearFilters = useCallback(() => {
    resetFilters();
    setLocalSearch('');
    setMaxPrice(0);
    setRooms('');
  }, [resetFilters]);

  const bannerList =
    banners && banners.length > 0
      ? banners
      : [{ id: 0, url_imagem: PLACEHOLDER_IMG } as IPropsBanners];

  return (
    <BannerRoot>
      <HeroColumn>
        <HeroCard $expanded={moreFilters}>
          <div>
            <HeroTitle>
              Encontre aqui seu próximo empreendimento de alto padrão
            </HeroTitle>
            <HeroSubtitle>
              Seja para morar ou para investir, mais de {rounded ?? 'X'}{' '}
              empreendimentos cadastrados, com curadoria e qualidade que apenas
              a Nogueira entrega.
            </HeroSubtitle>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <FieldLabel>
              Busca
              <input
                type="text"
                value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
                placeholder="Busque por Logradouro, Bairro, Cidade, Zona etc."
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearch();
                }}
              />
            </FieldLabel>

            <FieldLabel
              htmlFor="hero-construtora"
              $placeholderLike={construtoraEmpty}
            >
              Construtora
              <select
                id="hero-construtora"
                value={filters.id_constructor || ''}
                onChange={(e) => updateFilter('id_constructor', e.target.value)}
              >
                <option value="">Sua construtora favorita</option>
                {construtoras.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </FieldLabel>

            <FieldsRow>
              <FieldLabel>
                Valor até
                <input
                  type="text"
                  inputMode="numeric"
                  value={maskBRL(maxPrice)}
                  onChange={(e) => {
                    const inputValue =
                      Number(e.target.value.replace(/\D/g, '')) / 100;
                    setMaxPrice(Number.isFinite(inputValue) ? inputValue : 0);
                  }}
                  placeholder="R$ 0,00"
                />
              </FieldLabel>
              <FieldLabel>
                Quartos
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={rooms}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '') {
                      setRooms('');
                      return;
                    }
                    const n = Number(v);
                    if (!Number.isFinite(n) || n < 1) return;
                    setRooms(String(Math.floor(n)));
                  }}
                  placeholder="1"
                />
              </FieldLabel>
            </FieldsRow>
          </div>

          {!moreFilters && (
            <SearchButton type="button" onClick={handleSearch}>
              Buscar imóveis
            </SearchButton>
          )}

          <MoreFiltersToggle
            type="button"
            onClick={() => setMoreFilters((v) => !v)}
          >
            {moreFilters ? 'Ver menos filtros' : 'Ver mais filtros'}
          </MoreFiltersToggle>

          {moreFilters && (
            <ExpandedFilters>
              <ExpandedFiltersBody>
                <AdvancedSearch />
              </ExpandedFiltersBody>
              <ExpandedFiltersFooter>
                <ClearFiltersButton type="button" onClick={handleClearFilters}>
                  Limpar filtros
                </ClearFiltersButton>
                <SearchButton type="button" onClick={handleSearch}>
                  Buscar imóveis
                </SearchButton>
              </ExpandedFiltersFooter>
            </ExpandedFilters>
          )}
        </HeroCard>
      </HeroColumn>

      <BannerMedia>
        <CarouselANTD infinite autoplay autoplaySpeed={5000} dots arrows>
          {bannerList.map((banner, index) => (
            <img
              key={banner.id ?? index}
              src={banner.url_imagem || PLACEHOLDER_IMG}
              alt={`banner ${index + 1}`}
              className="banner-slide"
            />
          ))}
        </CarouselANTD>
      </BannerMedia>
    </BannerRoot>
  );
}
